import React, { useEffect, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useParams } from 'react-router';
import { RootState } from 'core/store';
import {
  IonAccordion,
  IonAccordionGroup,
  IonButton,
  IonContent,
  IonFab,
  IonFabButton,
  IonIcon,
  IonImg,
  IonItem,
  IonLabel,
  IonList,
  IonPage,
  IonPopover,
  IonSpinner,
  IonText,
  IonToolbar
} from '@ionic/react';
import {
  addOutline,
  chevronUpOutline,
  createOutline,
  ellipsisHorizontal,
  sparklesOutline,
  trashOutline
} from 'ionicons/icons';
import Close from 'assets/icons/close.svg';
import Image from 'assets/icons/image.svg';
import Documents from 'assets/icons/documents.svg';
import './InventoryDetailsMobile.scss';
import { Asset } from 'core/types';
import { gaModalView, getFallbackProductImage, viewDocument } from 'core/util';
import {
  hideItemDetailsModal,
  ProductGroup
} from 'features/shared/inventory/store/InventorySlice';
import {
  deleteProduct,
  fetchProductDetails,
  requestProductGroup
} from 'features/shared/inventory/store/InventoryActions';
import { HKDocumentType } from 'features/shared/documents/DocumentsSlice';
import { displayError } from 'core/services/AlertActions';
import { useProductPhotoActions } from 'features/shared/inventory/hooks/useProductPhotoActions';
import InventoryDetailSkeletonLoader from 'features/shared/inventory/components/InventoryDetailSkeletonLoader';
import InventoryProductEditForm, {
  InventoryProductEditFormHandle
} from 'features/shared/inventory/components/InventoryProductEditForm';
import InventoryDeleteConfirmModal from 'features/shared/inventory/components/InventoryDeleteConfirmModal';
import InventoryAnalyzeImageModal from 'features/shared/inventory/components/InventoryAnalyzeImageModal';
import { formatInventoryFieldValue } from 'features/shared/inventory/utils/formatInventoryFieldValue';
import 'swiper/css';
import 'swiper/css/pagination';
import '@ionic/react/css/ionic-swiper.css';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination } from 'swiper/modules';

interface ItemAttribute {
  label: string;
  value: string;
}

// The mobile modal is rendered from both InventoryProducts.tsx (route
// `/inventory/group/:id`) and InventoryGroups.tsx (route `/inventory`, no
// `:id`) — `id` is undefined in the latter case, so `group.group` falls back
// to '' there, same as InventoryDetailsDesktop.tsx's own unmatched-group case.
interface ItemParams {
  id?: string;
}

const InventoryDetailsMobile: React.FC = () => {
  const homeId = useSelector((state: RootState) => state.home.currentHome?.id);
  const { platformType } = useSelector((state: RootState) => state.platform);
  const { isDigital } = useSelector((state: RootState) => state.user);
  const {
    product,
    product_id,
    productDetailsLoading,
    productGroups,
    updateProductLoading
  } = useSelector((state: RootState) => state.inventory);
  const [attributes, setAttributes] = useState<ItemAttribute[]>([]);
  const [group, setGroup] = useState<ProductGroup>({
    id: 0,
    count: 0,
    group: ''
  });
  const [isEditing, setIsEditing] = useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [isAnalyzeModalOpen, setIsAnalyzeModalOpen] = useState(false);
  // Stable reference (never reassigned) so re-renders don't fight the
  // accordion's own open/closed state — mirrors InventoryDetailsDesktop.tsx.
  const [openAccordionValues] = useState(['product-details', 'documents']);
  const editFormRef = useRef<InventoryProductEditFormHandle>(null);
  const { id: groupId } = useParams<ItemParams>();
  const dispatch = useDispatch();
  const { addImage, analyzeImage, isAnalyzing } = useProductPhotoActions(
    homeId,
    product,
    () => setIsAnalyzeModalOpen(false)
  );
  const assets = !!product
    ? [...product.product_template.assets, ...product.assets]
    : [];
  const photoAssets = assets.filter(
    (asset) => asset.type === HKDocumentType.Photos
  );
  // Analyze-candidates are the product's OWN photos only — never catalog
  // template stock photos, which won't have this specific unit's serial/
  // model label on them.
  const ownPhotoAssets = (product?.assets || []).filter(
    (asset) => asset.type === HKDocumentType.Photos
  );

  const displayAssetThumbnail = (asset: Asset) => {
    return asset.thumb_cloud_store ? asset.thumb_cloud_store : asset.file_url;
  };

  const confirmAnalyzeImage = () => {
    if (!ownPhotoAssets.length) {
      dispatch(displayError('Add a photo first, then analyze it.'));
      return;
    }
    setIsAnalyzeModalOpen(true);
  };

  const closeItemDetailsModal = () => {
    dispatch(hideItemDetailsModal());
  };

  const confirmDelete = async () => {
    if (!!homeId && !!product) {
      // Awaited, and the group's product list is refetched explicitly: closing
      // this modal only refetches the group *counts* (InventoryProducts'
      // closeDetailsModal), so without this the deleted product stayed in the
      // list underneath. Awaiting also stops the refetch racing the DELETE.
      await dispatch(deleteProduct(homeId, product.id));
      if (!!groupId) {
        dispatch(requestProductGroup(homeId, groupId));
      }
    }
    setIsDeleteConfirmOpen(false);
    closeItemDetailsModal();
  };

  // Exact same field set/labels as InventoryDetailsDesktop.tsx's attributes —
  // borrowed verbatim per the mobile restyle's Figma spec (node 8731:5243).
  useEffect(() => {
    if (!!product) {
      setAttributes([
        { label: 'Group', value: group.group },
        {
          label: 'Product Type',
          value: product.product_template.type_data.label
        },
        {
          label: 'Brand/Manufacturer',
          value: formatInventoryFieldValue(
            product.product_template.manufacturer?.name
          )
        },
        {
          label: 'Model #',
          value: formatInventoryFieldValue(product.product_template.model)
        },
        { label: 'Serial #', value: product.serial || 'N/A' },
        { label: 'Registered', value: product.registered ? 'Yes' : 'No' }
      ]);
    }
  }, [dispatch, product, group]);

  useEffect(() => {
    if (!product && !!product_id && !!homeId) {
      dispatch(fetchProductDetails(homeId, Number(product_id)));
    }
  }, [dispatch, product, homeId, product_id]);

  useEffect(() => {
    if (!!productGroups && !group.count) {
      const g = productGroups?.find((g) => g.id === Number(groupId));
      if (!!g) {
        setGroup(g);
      }
    }
  }, [productGroups, groupId]);

  useEffect(() => {
    gaModalView(isDigital, '/inventory/group/product');
  }, []);

  const manualDocuments = assets.filter(
    (asset) => asset.type === HKDocumentType.Manuals
  );

  return (
    <IonPage>
      {productDetailsLoading || !product ? (
        <InventoryDetailSkeletonLoader />
      ) : (
        <>
          <IonContent className="hk-digital-item-detail">
            <div className="hk-digital-item-detail-image-media">
              {photoAssets.length ? (
                <>
                  <Swiper
                    className="hk-digital-item-detail-image-slider"
                    modules={[Pagination]}
                    pagination={{
                      el: '.hk-digital-item-detail-image-pagination',
                      clickable: true
                    }}
                  >
                    {photoAssets.map((asset) => (
                      <SwiperSlide key={asset.id}>
                        <img src={displayAssetThumbnail(asset)} alt="Product" />
                      </SwiperSlide>
                    ))}
                  </Swiper>
                  <div className="hk-digital-item-detail-image-pagination" />
                </>
              ) : (
                <img
                  src={
                    product?.product_template.type
                      ? getFallbackProductImage(product?.product_template.type)
                      : Image
                  }
                  alt="No product"
                  className="hk-image-placeholder"
                />
              )}
              <IonFab
                vertical="top"
                horizontal="start"
                className="hk-digital-item-detail-controls-fab"
              >
                <IonFabButton
                  className="hk-digital-item-detail-controls-button"
                  id="hk-inventory-details-mobile-more-actions"
                >
                  <IonIcon color="dark" icon={ellipsisHorizontal} />
                </IonFabButton>
                <IonPopover
                  trigger="hk-inventory-details-mobile-more-actions"
                  triggerAction="click"
                  dismissOnSelect={true}
                  className="hk-digital-item-detail-actions-popover"
                >
                  <IonList
                    lines="full"
                    className="hk-digital-item-detail-actions-list"
                  >
                    <IonItem
                      button
                      detail={false}
                      onClick={() => setIsEditing(true)}
                    >
                      <IonIcon slot="start" icon={createOutline} />
                      <IonLabel>Edit Details</IonLabel>
                    </IonItem>
                    <IonItem button detail={false} onClick={addImage}>
                      <IonIcon slot="start" icon={addOutline} />
                      <IonLabel>Add Image</IonLabel>
                    </IonItem>
                    <IonItem
                      button
                      detail={false}
                      onClick={confirmAnalyzeImage}
                    >
                      <IonIcon slot="start" icon={sparklesOutline} />
                      <IonLabel>Analyze Image</IonLabel>
                    </IonItem>
                    <IonItem
                      button
                      detail={false}
                      className="hk-digital-item-detail-actions-delete"
                      onClick={() => setIsDeleteConfirmOpen(true)}
                    >
                      <IonIcon slot="start" icon={trashOutline} />
                      <IonLabel>Delete Item</IonLabel>
                    </IonItem>
                  </IonList>
                </IonPopover>
              </IonFab>
              <IonFab
                vertical="top"
                horizontal="end"
                className="hk-digital-item-detail-controls-fab"
              >
                <IonFabButton
                  className="hk-digital-item-detail-controls-button"
                  onClick={() => closeItemDetailsModal()}
                >
                  <IonIcon color="dark" icon={Close} />
                </IonFabButton>
              </IonFab>
            </div>
            <div className="hk-digital-item-detail-container">
              <IonAccordionGroup
                multiple={true}
                value={openAccordionValues}
                className="hk-digital-item-detail-accordion-group"
              >
                <IonAccordion
                  value="product-details"
                  className="hk-digital-item-detail-accordion"
                  toggleIcon={chevronUpOutline}
                >
                  <IonItem
                    slot="header"
                    lines="none"
                    className="hk-digital-item-detail-accordion-header"
                  >
                    <IonLabel className="hk-digital-item-detail-accordion-title">
                      Product Details
                    </IonLabel>
                  </IonItem>
                  <div
                    slot="content"
                    className="hk-digital-item-detail-accordion-content"
                  >
                    {isEditing ? (
                      <InventoryProductEditForm
                        ref={editFormRef}
                        product={product}
                        onSaved={() => setIsEditing(false)}
                      />
                    ) : (
                      attributes.map((att) => {
                        const { label, value } = att;
                        return (
                          <div
                            key={label}
                            className="hk-digital-item-detail-attribute"
                          >
                            <IonText className="hk-digital-item-detail-attribute-label">
                              {label}
                            </IonText>
                            <div className="hk-digital-item-detail-attribute-value">
                              {value}
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                </IonAccordion>
                {!isEditing && !!manualDocuments.length && (
                  <IonAccordion
                    value="documents"
                    className="hk-digital-item-detail-accordion"
                    toggleIcon={chevronUpOutline}
                  >
                    <IonItem
                      slot="header"
                      lines="none"
                      className="hk-digital-item-detail-accordion-header"
                    >
                      <IonLabel className="hk-digital-item-detail-accordion-title">
                        Documents
                      </IonLabel>
                    </IonItem>
                    <div
                      slot="content"
                      className="hk-digital-item-detail-accordion-content"
                    >
                      <div className="hk-digital-item-detail-document-slider">
                        {manualDocuments.map((attr) => {
                          const { id, name, file_url } = attr;
                          return (
                            <div
                              className="hk-digital-item-detail-document-slider-slide"
                              key={id}
                              role="button"
                              tabIndex={0}
                              onClick={() =>
                                viewDocument(file_url, platformType)
                              }
                              onKeyDown={(event) => {
                                if (
                                  event.key === 'Enter' ||
                                  event.key === ' '
                                ) {
                                  event.preventDefault();
                                  viewDocument(file_url, platformType);
                                }
                              }}
                            >
                              <IonImg
                                src={Documents}
                                className="hk-digital-item-detail-document-slider-slide-img"
                              />
                              <IonText
                                color="medium"
                                className="hk-digital-item-detail-document-slider-slide-name ion-text-center"
                              >
                                {name}
                              </IonText>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </IonAccordion>
                )}
              </IonAccordionGroup>
            </div>
          </IonContent>
          {isEditing ? (
            <IonToolbar className="hk-digital-item-toolbar hk-digital-item-toolbar-edit">
              <div className="hk-digital-item-detail-edit-actions">
                <IonButton
                  expand="block"
                  fill="outline"
                  className="hk-digital-item-detail-edit-cancel"
                  disabled={updateProductLoading}
                  onClick={() => setIsEditing(false)}
                >
                  Cancel
                </IonButton>
                <IonButton
                  expand="block"
                  color="primary"
                  strong={true}
                  disabled={updateProductLoading}
                  onClick={() => editFormRef.current?.save()}
                >
                  {updateProductLoading ? <IonSpinner name="dots" /> : 'Save'}
                </IonButton>
              </div>
            </IonToolbar>
          ) : (
            <IonToolbar className="hk-digital-item-toolbar">
              <IonButton
                expand="block"
                color="primary"
                strong={true}
                onClick={() => closeItemDetailsModal()}
              >
                Done
              </IonButton>
            </IonToolbar>
          )}
        </>
      )}
      <InventoryDeleteConfirmModal
        isOpen={isDeleteConfirmOpen}
        productName={product?.name || product?.product_template.name}
        productModel={product?.product_template.model}
        productBrand={product?.product_template.manufacturer?.name}
        productType={product?.product_template.type}
        onCancel={() => setIsDeleteConfirmOpen(false)}
        onConfirm={confirmDelete}
      />
      <InventoryAnalyzeImageModal
        isOpen={isAnalyzeModalOpen}
        photos={ownPhotoAssets}
        loading={isAnalyzing}
        onCancel={() => setIsAnalyzeModalOpen(false)}
        onConfirm={analyzeImage}
      />
    </IonPage>
  );
};

export default InventoryDetailsMobile;
